import { ChangeDetectionStrategy, Component, ComponentRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewContainerRef, inject } from '@angular/core';
import { MetaInfo } from '../../version-control/meta-info.model';
import { InventoryChangesetItem } from './inventory-changeset-item.model';
import { UserStateService } from '../../common/services/user/user.state.service';
import { DiffViewComponent } from '../../common/model/publish-changes/diff-view-component.model';
import { PublishInventoryContext } from './publish-inventory.context';
import { Dictionary } from '../../model/reducer';
import { SplitPaneConfig } from '../../common/model/split-pane-config.model';
import { SplitGutterInteractionEvent } from 'angular-split';
import { ResizeHelper } from '../../common/helpers/resize.helper';
import { Mixin } from '../../common/decorators/mixin.decorator';
import { ISplitMixin, SplitMixin } from '../../common/mixins/split.mixin';
import { PublishInventoryInput } from '../inventory.model';

@Component({
    selector: 'adm4-publish-inventory',
    templateUrl: './publish-inventory.component.html',
    styleUrls: ['./publish-inventory.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
@Mixin([SplitMixin])
export class PublishInventoryComponent implements ISplitMixin, OnInit, OnChanges {
  private inventoryPublishContext = inject(PublishInventoryContext);
  private userStateService = inject(UserStateService);

  @Input() changesetItems: InventoryChangesetItem<MetaInfo, any>[];
  @Input() initialCommitMessage = '';
  @Output() isDirty: EventEmitter<boolean> = new EventEmitter();
  @Output() publish: EventEmitter<PublishInventoryInput> = new EventEmitter();
  @Output() cancel: EventEmitter<undefined> = new EventEmitter();
  @ViewChild('diffViewContainer', {read: ViewContainerRef, static: false}) diffViewContainer: ViewContainerRef;

  userInput: PublishInventoryInput;
  currentUsername: string | null;
  selectedChangesetItem?: InventoryChangesetItem<MetaInfo, any>;

  readonly listSplitAreaKey = 'list';
  readonly diffViewSplitAreaKey = 'diff-view';
  readonly splitPaneConfigLocalStorageKey = 'publish-inventory-splitpane-config';
  splitPaneConfig: Dictionary<SplitPaneConfig> = {
    [this.listSplitAreaKey]: {order: 0, size: 30},
    [this.diffViewSplitAreaKey]: {order: 1, size: 70}
  };

  /**
   * Implemented by SplitMixin
   */
  onResize: (event: SplitGutterInteractionEvent) => void;

  constructor() {
    this.currentUsername = this.userStateService.username;
    this.splitPaneConfig = ResizeHelper.retrieveSplitPaneConfig(this.splitPaneConfigLocalStorageKey, this.splitPaneConfig);
  }

  ngOnInit(): void {
    this.inventoryPublishContext.preloadDiffs();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.initialCommitMessage) {
      this.userInput = {commitMessage: this.initialCommitMessage, tags: []};
    }
  }

  onChangesetItemSelected(changesetItem: InventoryChangesetItem<MetaInfo, any>): void {
    this.selectedChangesetItem = changesetItem;
    this.generateDynamicDiffView(changesetItem);
  }

  private generateDynamicDiffView(changesetItem: InventoryChangesetItem<MetaInfo, any>): void {
    this.diffViewContainer.clear();
    const componentRef: ComponentRef<DiffViewComponent<InventoryChangesetItem<MetaInfo, any>>>
        = this.diffViewContainer.createComponent(changesetItem?.diffComponent);
    componentRef.instance.changesetItem = changesetItem;
  }

  onUserInputChanged(input: PublishInventoryInput): void {
    this.userInput = input;
    const dirty: boolean = this.userInput.commitMessage !== this.initialCommitMessage || this.userInput.tags.length > 0;
    this.isDirty.emit(dirty);
  }

  triggerPublish(): void {
    this.publish.emit(this.userInput);
  }

  triggerCancel(): void {
    this.cancel.emit();
  }

}
